// Day-by-day follow-up of a running program: what each step prescribes against
// what the horse's sessions did, one row per program day. Pure, no '@/' import.
// Only the horse's owners see it (canTrackProgram): shared members and the vet
// do not see the horse's sessions, so every ridden day would read "no session".
//
// A work day is 'done' when every prescribed gait (walk, trot, canter) reaches
// 80 % of its duration, 'partial' when a session happened below that, 'none'
// without a session. Today without a session carries no status, a future day is
// 'upcoming'. A rest day is 'rest', or 'rest-not-respected' once a session
// happened on it. Standing never counts. A gait that was not prescribed is info only.

import { finite } from '../sessions/gait-total.js'
import { hasRestFlag, isFlaggedRest } from './rest-day.js'
import { pad } from '../helpers/numeric.js'

export const DONE_THRESHOLD = 0.8
export const TRACKED_GAITS = ['walk', 'trot', 'canter']

// The API sends start_at as a local calendar day ('YYYY-MM-DD' or
// 'YYYY-MM-DDT00:00:00'). Date.parse would anchor a date-only string to UTC
// midnight, one day off west of UTC: read the digits as a local day instead.
export const parseLocalDay = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''))
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))

    const timestamp = Date.parse(value ?? '')
    if (!Number.isFinite(timestamp)) return null
    const date = new Date(timestamp)
    date.setHours(0, 0, 0, 0)
    return date
}

// Calendar days from `from` to `to`, on local dates: 0 on the same day, 1 the
// next one. Counted through UTC so a DST change never shifts the result.
export const calendarDaysBetween = (from, to) => {
    const utcDay = (date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    return Math.round((utcDay(to) - utcDay(from)) / 86_400_000)
}

// Days a calendar event of /user/events spans, both ends included: 1 for a single
// day. Events, programs and protocols arrive as local midnights, so across a spring
// DST change their gap is an hour short of whole days: that hour is given back
// before truncating. A session (real instants) keeps one day per started 24 h, so
// 23:50 → 00:20 stays on one day (rounding would put a 12 h session on two).
export const eventDayCount = (startAt, endAt) =>
    Math.trunc((new Date(endAt) - new Date(startAt) + 3_600_000) / 86_400_000) + 1

// A whole number above 0 (a day number, an id), or null for anything else.
export const toPositiveInt = (value) => {
    const number = Number(value)
    return Number.isInteger(number) && number > 0 ? number : null
}

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
// The local calendar day of a date, 'YYYY-MM-DD'.
export const dayKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
const dayCount = (step) => Math.max(0, Math.trunc(finite(step?.day_count)))

// Sum of the prescribed seconds per gait over a step's gait rows (one day of work).
export const prescribedByGait = (step) => {
    const totals = { walk: 0, trot: 0, canter: 0 }
    for (const row of step?.gaits ?? []) {
        if (TRACKED_GAITS.includes(row?.gait)) totals[row.gait] += finite(row.duration)
    }
    return totals
}

// P7: the step says whether it is a rest day (`is_rest`). Only a payload that
// does not carry the flag at all (an old cached one) falls back on "no prescribed
// walk, trot nor canter = rest", here and only here (display fallback).
export const isRestStep = (step) => {
    if (hasRestFlag(step)) return isFlaggedRest(step)
    return TRACKED_GAITS.every((gait) => prescribedByGait(step)[gait] <= 0)
}

// Instant (UTC 'YYYY-MM-DD HH:mm:ss') of a local midnight, for the API range.
const utcInstant = (date) =>
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`

// The sessions to fetch: from the first program day to the day after the last
// one, both at local midnight, so a late session on the last day is included.
export const programSessionsRange = ({ start_at, steps } = {}) => {
    const start = parseLocalDay(start_at)
    const total = (steps ?? []).reduce((sum, step) => sum + dayCount(step), 0)
    if (!start || !total) return null
    return { from: utcInstant(start), to: utcInstant(addDays(start, total)) }
}

const dayStatus = ({ isRest, isFuture, isToday, prescribed, done, sessionCount }) => {
    if (isFuture) return 'upcoming'
    if (isRest) return sessionCount ? 'rest-not-respected' : 'rest'
    if (!sessionCount) return isToday ? null : 'none'
    // Compared in tenths so 80 % of 25 min (1200 s) is not lost to float rounding.
    const reached = TRACKED_GAITS.every((gait) => done[gait] * 10 >= prescribed[gait] * DONE_THRESHOLD * 10)
    return reached ? 'done' : 'partial'
}

// The horse's real owners only: `can_edit` AND listed in the Horse resource's
// `owners`. The API also sets `can_edit` for every admin, but an admin who is
// not linked to the horse does not get its sessions from /user/sessions, so
// every ridden day would read "no session". A missing field hides the card.
export const canTrackProgram = (horse, userId) =>
    horse?.can_edit === true &&
    userId != null &&
    (horse?.owners ?? []).some((owner) => owner?.id != null && Number(owner.id) === Number(userId))

// One row per program day, in calendar order: day 1 first, then today, then the
// days to come ('upcoming'). Nothing before the program starts.
export const buildProgramDays = ({ start_at, steps } = {}, sessions = [], today = new Date()) => {
    const start = parseLocalDay(start_at)
    if (!start || !(steps?.length > 0)) return []

    const todayKey = dayKey(today)
    const sessionsByDay = new Map()
    for (const session of sessions ?? []) {
        const timestamp = Date.parse(session?.start_at ?? '')
        if (!Number.isFinite(timestamp)) continue
        const key = dayKey(new Date(timestamp))
        sessionsByDay.set(key, [...(sessionsByDay.get(key) ?? []), session])
    }
    // The API lists the latest session first: a day reads in the order it was ridden.
    for (const daySessions of sessionsByDay.values()) {
        daySessions.sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at))
    }

    const days = []
    steps.forEach((step, stepIndex) => {
        const isRest = isRestStep(step)
        const prescribed = prescribedByGait(step)
        for (let i = 0; i < dayCount(step); i++) {
            const date = addDays(start, days.length)
            const key = dayKey(date)
            const daySessions = sessionsByDay.get(key) ?? []
            const done = Object.fromEntries(
                TRACKED_GAITS.map((gait) => [
                    gait,
                    daySessions.reduce((sum, s) => sum + finite(s?.[`${gait}_time`]), 0),
                ])
            )
            const isToday = key === todayKey
            const isFuture = key > todayKey
            days.push({
                key,
                date,
                dayNumber: days.length + 1,
                stepIndex,
                isRest,
                isToday,
                isFuture,
                gaits: isRest
                    ? []
                    : TRACKED_GAITS.filter((g) => prescribed[g] > 0).map((g) => ({
                          gait: g,
                          prescribed: prescribed[g],
                          done: done[g],
                      })),
                // What was done beyond the prescription (every gait on a rest day).
                extras: TRACKED_GAITS.filter((g) => (isRest || !(prescribed[g] > 0)) && done[g] > 0).map((g) => ({
                    gait: g,
                    done: done[g],
                })),
                sessions: daySessions,
                status: dayStatus({ isRest, isFuture, isToday, prescribed, done, sessionCount: daySessions.length }),
            })
        }
    })

    if (!days.some((day) => !day.isFuture)) return []
    return days.map((day) => ({ ...day, totalDays: days.length }))
}

// The dot of a row: its status, 'today' while today has no verdict yet (no
// session, or a rest day still running), and a rest day kept counts as done.
export const dayDot = (day) => {
    if (day?.status === 'rest') return day.isToday ? 'today' : 'done'
    return day?.status ?? 'today'
}

// The gaits a row lists, in whole minutes: what was done on a past day, done out
// of prescribed today, what is prescribed on a day to come; then what was done
// beyond the prescription.
const toMinutes = (seconds) => Math.round(finite(seconds) / 60)
const gaitFigure = (day, { done, prescribed }) => {
    if (day.isFuture) return `${toMinutes(prescribed)}`
    if (day.isToday) return `${toMinutes(done)}/${toMinutes(prescribed)}`
    return `${toMinutes(done)}`
}
export const dayGaitFigures = (day) => [
    ...(day?.gaits ?? []).map((row) => ({ gait: row.gait, text: gaitFigure(day, row) })),
    ...(day?.extras ?? []).map((row) => ({ gait: row.gait, text: `${toMinutes(row.done)}` })),
]

// A long program keeps the card short: only the last RECENT_DAYS days up to
// today show, the earlier ones fold behind a link at the top and unfold above
// them in the same calendar order; after today, the next UPCOMING_DAYS show and
// the later ones fold behind a link at the bottom. `hiddenCount` and `laterCount`
// are how many days each fold holds (0 when everything fits, then no link). The
// header still counts every day.
export const RECENT_DAYS = 7
export const UPCOMING_DAYS = 3

export const visibleProgramDays = (days = [], earlier = false, later = false) => {
    const list = Array.isArray(days) ? days : []
    const past = list.filter((day) => !day.isFuture)
    const upcoming = list.filter((day) => day.isFuture)
    const hiddenCount = Math.max(0, past.length - RECENT_DAYS)
    const laterCount = Math.max(0, upcoming.length - UPCOMING_DAYS)
    return {
        days: [...(earlier ? past : past.slice(hiddenCount)), ...(later ? upcoming : upcoming.slice(0, UPCOMING_DAYS))],
        hiddenCount,
        laterCount,
    }
}

// Header figures: today's day number (null outside the program) and the days done:
// every green dot (dayDot 'done'), a past rest day kept included, so the header
// and the dots never disagree.
export const programProgress = (days) => ({
    dayNumber: days.find((day) => day.isToday)?.dayNumber ?? null,
    totalDays: days[0]?.totalDays ?? 0,
    doneCount: days.filter((day) => dayDot(day) === 'done').length,
})
